import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem
} from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useUsuarioStore } from '../store/usuarioStore';
import { permisoService } from '../services/permisoService';
import { moduloService, type Modulo } from '../services/moduloService';
import type { Permiso } from '../types/usuario.types';
import { initialPermisoState } from '../types/usuario.types';

export const PermisosPage = () => {
  const { permisos, setPermisos, setLoading, removePermiso, addPermiso, updatePermiso } = useUsuarioStore();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPermiso, setSelectedPermiso] = useState<Permiso>(initialPermisoState);
  const [modulos, setModulos] = useState<Modulo[]>([]);

  useEffect(() => {
    loadPermisos();
    loadModulos();
  }, []);

  const loadPermisos = async () => {
    setLoading(true);
    try {
      const data = await permisoService.getAll();
      setPermisos(data);
    } finally {
      setLoading(false);
    }
  };

  const loadModulos = async () => {
    try {
      const data = await moduloService.getAll();
      setModulos(data);
    } catch (error) {
      console.error('Error al cargar modulos:', error);
    }
  };

  const handleNew = () => {
    setSelectedPermiso({ ...initialPermisoState, idModulo: modulos[0]?.id || 0 });
    setOpenDialog(true);
  };

  const handleEdit = (permiso: Permiso) => {
    setSelectedPermiso(permiso);
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Esta seguro de eliminar este permiso?')) {
      try {
        await permisoService.delete(id);
        removePermiso(id);
      } catch (error) {
        console.error('Error al eliminar permiso:', error);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const saved = await permisoService.save(selectedPermiso);

      if (selectedPermiso.id === 0) {
        addPermiso(saved);
      } else {
        updatePermiso(saved);
      }

      setOpenDialog(false);
      setSelectedPermiso(initialPermisoState);
      loadPermisos();
    } catch (error) {
      console.error('Error al guardar permiso:', error);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'codigo', headerName: 'Codigo', flex: 1 },
    { field: 'descripcion', headerName: 'Descripcion', flex: 2 },
    {
      field: 'modulo',
      headerName: 'Modulo',
      flex: 1,
      valueGetter: (_value, row) => row.modulo?.descripcion || ''
    },
    {
      field: 'esOculto',
      headerName: 'Oculto',
      width: 100,
      type: 'boolean'
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Editar"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Eliminar"
          onClick={() => handleDelete(params.row.id)}
        />
      ]
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5">Gestion de Permisos</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNew}
          >
            Nuevo Permiso
          </Button>
        </Box>

        <DataGrid
          rows={permisos}
          columns={columns}
          autoHeight
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          pageSizeOptions={[5, 10, 25]}
          disableRowSelectionOnClick
        />
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedPermiso.id === 0 ? 'Nuevo Permiso' : 'Editar Permiso'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Codigo"
                value={selectedPermiso.codigo}
                onChange={(e) => setSelectedPermiso({ ...selectedPermiso, codigo: e.target.value })}
                required
                helperText="Ej: GUIA_CREAR, USUARIO_EDITAR"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Descripcion"
                value={selectedPermiso.descripcion}
                onChange={(e) => setSelectedPermiso({ ...selectedPermiso, descripcion: e.target.value })}
                required
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Modulo</InputLabel>
                <Select
                  value={selectedPermiso.idModulo}
                  label="Modulo"
                  onChange={(e) => setSelectedPermiso({ ...selectedPermiso, idModulo: e.target.value as number })}
                >
                  {modulos.map((modulo) => (
                    <MenuItem key={modulo.id} value={modulo.id}>
                      {modulo.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedPermiso.esOculto}
                    onChange={(e) => setSelectedPermiso({ ...selectedPermiso, esOculto: e.target.checked })}
                  />
                }
                label="Oculto"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedPermiso.id === 0 ? 'Crear' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermisosPage;
